import "./style.scss";

import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Link, LinkTextDecorationOptions } from "@amzn/stencil-react-components/link";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { BaseClickPayload, Bucket, trackRecommendedProductClicked } from "common/helpers/search-analytics";
import { TrafficFrom } from "common/helpers/traffic-from";
import { CatalogItemSimplified } from "common/types/catalog-types";
import NoLogoImage from "img/no_logo_image.png";
import { SearchExperienceService } from "lib/services/shop-search-service";
import { ItemTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import React, { FC, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { FoundationalChip } from "../FoundationalChip";
import { PageMainContainer } from "../PageMainContainer/PageMainContainer";

export interface RecommendedProductsProps {
  /**
   * Categories of the current product
   */
  categories: string[];

  /**
   * Current product taxonomy ID (to exclude if it's foundational)
   */
  currentProductTaxonomyId: string;

  /**
   * Whether the current product is foundational
   */
  isCurrentProductFoundational: boolean;

  /**
   * Callback to notify parent when products are loaded or empty
   */
  onProductsLoaded?: (hasProducts: boolean) => void;
}

export const RecommendedProducts: FC<RecommendedProductsProps> = ({
  categories,
  currentProductTaxonomyId,
  isCurrentProductFoundational,
  onProductsLoaded,
}) => {
  const [foundationalProducts, setFoundationalProducts] = useState<CatalogItemSimplified[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();

  useEffect(() => {
    fetchFoundationalProducts();
  }, [categories, currentProductTaxonomyId]);

  useEffect(() => {
    if (!isLoading && onProductsLoaded) {
      onProductsLoaded(foundationalProducts.length > 0);
    }
  }, [foundationalProducts, isLoading, onProductsLoaded]);

  const fetchFoundationalProducts = async () => {
    if (!categories || categories.length === 0) {
      return;
    }

    setIsLoading(true);
    try {
      // Use SearchExperienceService to make the API call
      const searchService = new SearchExperienceService("software");

      // Filter out "Top-picks" category as it's a special category that shouldn't be used in the search
      const filteredCategories = categories.filter((category) => category !== "Top-picks");

      // If no valid categories remain after filtering, don't make the request
      if (filteredCategories.length === 0) {
        setFoundationalProducts([]);
        return;
      }

      // Build filter for foundational products with matching categories
      const filters = {
        categories: filteredCategories,
        itptClassification: ["Foundational"],
      };

      const params = {
        limit: 3,
        offset: 0,
        filter: JSON.stringify(filters),
        itemType: "software",
        searchQuery: "",
        useSemanticSearch: true,
        useIntent: true,
        simplifiedResults: true,
        caseInsensitive: true,
        sortBy: "name",
        sortDirection: "asc",
      };

      const data = await searchService.getItems(params);
      let products = data.data || [];

      // Always filter out the current product
      products = products.filter((p: any) => p.taxonomyId !== currentProductTaxonomyId);

      // Limit to 3 products
      const limitedProducts = products.slice(0, 3).map((item: any) => ({
        itemId: item.taxonomyId,
        name: item.name,
        shortDescription: item.shortDescription,
        logoImageUrl: item.logoUrl,
        categories: item.categories,
        tags: item.tags,
        status: item.status,
        etaDays: item.etaDays,
        hasStock: item.hasStock,
        itptClassification: item.itptClassification,
      }));

      setFoundationalProducts(limitedProducts);
    } catch (error) {
      console.error("Error fetching foundational products:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductClick = (event: any, taxonomyId: string, productName: string, rank: number) => {
    // Track UXM event for recommended product click
    const payload: BaseClickPayload = {
      taxonomyId: taxonomyId,
      name: productName,
      rank: rank,
      queryId: null, // No query ID for recommended products
      bucket: Bucket.Foundational, // All recommended products are foundational
    };
    trackRecommendedProductClicked(payload);

    try {
      const detailsUrl = `/items/${ItemTaxonomyId.Parse(taxonomyId).getUrlSuffix()}?from=${
        TrafficFrom.RecommendedProducts
      }`;
      if (event.ctrlKey || event.metaKey) return true;
      history.push(detailsUrl);
      event.preventDefault();
    } catch (error) {
      console.error("Error navigating to product:", error);
    }
  };

  if (isLoading) {
    return (
      <PageMainContainer className="recommended-products-section" paddingBottom="S500" paddingTop="S500">
        <View display="flex" justifyContent="center" alignItems="center" padding="S500">
          <Spinner size={SpinnerSize.Medium} />
        </View>
      </PageMainContainer>
    );
  }

  if (foundationalProducts.length === 0) {
    return null;
  }

  return (
    <PageMainContainer className="recommended-products-section" paddingBottom="S200" style={{ paddingTop: "40px" }}>
      <View>
        <Text
          fontSize="T300"
          fontWeight="bold"
          color="neutral90"
          style={{ marginBottom: "24px", fontSize: "20px", lineHeight: "32px" }}
        >
          Check recommended products
        </Text>
        <Row gridGap="S500" className="recommended-products-list">
          {foundationalProducts.map((product, index) => {
            const detailsUrl = `items/${ItemTaxonomyId.Parse(product.itemId!).getUrlSuffix()}?from=${
              TrafficFrom.RecommendedProducts
            }`;

            return (
              <Col key={product.itemId} className="recommended-product-card">
                <Link
                  href={detailsUrl}
                  onClick={(e) => handleProductClick(e, product.itemId!, product.name!, index + 1)}
                  textDecoration={LinkTextDecorationOptions.None}
                >
                  <Row gridGap="S300" alignItems="flex-start">
                    {/* Logo */}
                    <View className="recommended-product-logo-wrapper">
                      <img
                        src={product.logoImageUrl?.trim() !== "" ? product.logoImageUrl : NoLogoImage}
                        alt={`${product.name} logo`}
                        className="recommended-product-logo"
                      />
                    </View>

                    {/* Product info */}
                    <Col flex={1} gridGap="S100">
                      <Row alignItems="center" gridGap="S200" flexWrap="wrap">
                        <Text fontSize="T200" fontWeight="bold" color="neutral90" lineHeight="24px">
                          {product.name}
                        </Text>
                        <FoundationalChip />
                      </Row>
                      <Text
                        fontSize="T100"
                        fontWeight="regular"
                        color="neutral70"
                        lineHeight="20px"
                        className="recommended-product-description"
                      >
                        {product.shortDescription}
                      </Text>
                    </Col>
                  </Row>
                </Link>
              </Col>
            );
          })}
        </Row>
      </View>
    </PageMainContainer>
  );
};
